import { AlertTriangle, Inbox, Loader2 } from "lucide-react";

/** The three states every screen has besides its content. One look across all of them. */

export function Loading({ what = "Loading" }: { what?: string }) {
  return (
    <div className="state-box" role="status" aria-live="polite">
      <Loader2 className="spin" aria-hidden="true" />
      <div className="state-title">{what}...</div>
    </div>
  );
}

export function Empty({ title, children }: { title: string; children?: React.ReactNode }) {
  return (
    <div className="state-box">
      <Inbox aria-hidden="true" />
      <div>
        <div className="state-title">{title}</div>
        {children && <p className="muted">{children}</p>}
      </div>
    </div>
  );
}

export function ErrorState({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const message = error instanceof Error ? error.message : String(error);
  return (
    <div className="state-box fail" role="alert">
      <AlertTriangle aria-hidden="true" />
      <div>
        <div className="state-title">This could not be loaded</div>
        <p className="muted">{message}</p>
        {onRetry && <button className="ghost" onClick={onRetry}>Try again</button>}
      </div>
    </div>
  );
}

/** Where a list fetched on open stands: still on its way, answered, or failed before any answer. */
export type ListStatus = "loading" | "ready" | Error;

/**
 * What a list says in place of its rows: a loading line until the request answers, the failure if
 * it failed, and `empty` only when it answered with nothing. Null when there are rows to show.
 */
export function listNote(status: ListStatus, count: number, empty: string, what = "Loading"): string | null {
  if (status === "loading") return `${what}...`;
  if (status instanceof Error) return count === 0 ? `This could not be loaded: ${status.message}` : null;
  return count === 0 ? empty : null;
}
